import React, { useState } from "react";
import { toast } from "sonner";
import { Layers, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../ui/dialog";

const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const STATUS = {
  COMPUTED: ["Calculé", "text-[#1F4E3D]"],
  SKIPPED_FINALIZED: ["Ignoré : rapport finalisé", "text-[#575A57]"],
  SKIPPED_PAID: ["Ignoré : déjà payé", "text-[#575A57]"],
  MISSING_NAYAX: ["Récap Nayax manquant", "text-[#A04E2C]"],
  MISSING_TWINT: ["Nb transactions TWINT manquant", "text-[#A04E2C]"],
  ERROR: ["Erreur", "text-[#C05C47]"],
};
const curYear = new Date().getFullYear();

function Pick({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="mb-1 block text-xs">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid}><SelectValue placeholder="Choisir…" /></SelectTrigger>
        <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

export default function BatchNayaxComputeDialog({ contracts, labelOf, onDone }) {
  const [open, setOpen] = useState(false);
  const [contractId, setContractId] = useState("");
  const [year, setYear] = useState(String(curYear));
  const [mFrom, setMFrom] = useState("1");
  const [mTo, setMTo] = useState(String(Math.max(1, new Date().getMonth())));
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  const run = async () => {
    const c = contracts.find((x) => x.id === contractId);
    setBusy(true); setResults(null);
    try {
      const { data } = await api.post(
        `/statistics/nayax-prefill/commission-batch?distributor_id=${c.distributor_id}&year=${year}&month_from=${mFrom}&month_to=${mTo}`,
        {}, { timeout: 120000 },
      );
      setResults(data.results);
      toast.success(`${data.results.filter((r) => r.status === "COMPUTED").length} mois calculé(s)`);
      onDone?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Calcul impossible");
    } finally { setBusy(false); }
  };

  const monthOpts = MONTHS.map((l, i) => [String(i + 1), l]);
  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) setResults(null); }}>
      <DialogTrigger asChild>
        <Button variant="outline" disabled={!contracts.length} data-testid="pc-batch-nayax-btn">
          <Layers className="w-4 h-4 mr-1" /> Calculer plusieurs mois depuis Nayax
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="pc-batch-nayax-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Calculer plusieurs mois depuis Nayax</DialogTitle>
          <DialogDescription>
            Chaque mois est calculé avec les récaps Nayax importés (CA, cartes, TWINT, transactions TWINT).
            Les rapports finalisés ou déjà payés ne sont pas touchés. Les encaissements directs déjà saisis sont conservés.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Pick label="Contrat" value={contractId} onChange={setContractId} testid="pc-batch-contract"
              options={contracts.map((c) => [c.id, labelOf(c)])} />
          </div>
          <Pick label="Année" value={year} onChange={setYear} testid="pc-batch-year"
            options={[curYear, curYear - 1, curYear - 2].map((y) => [String(y), String(y)])} />
          <div />
          <Pick label="Du mois" value={mFrom} onChange={setMFrom} testid="pc-batch-from" options={monthOpts} />
          <Pick label="Au mois (inclus)" value={mTo} onChange={setMTo} testid="pc-batch-to" options={monthOpts} />
        </div>
        {results && (
          <table className="w-full text-sm mt-2" data-testid="pc-batch-results">
            <tbody>
              {results.map((r) => {
                const [label, cls] = STATUS[r.status] || [r.status, ""];
                return (
                  <tr key={r.month} className="border-t border-[#EEEEE8]">
                    <td className="py-1.5 font-mono text-xs">{r.month}</td>
                    <td className={`py-1.5 ${cls}`}>{label}{r.error ? ` — ${r.error}` : ""}</td>
                    <td className="py-1.5 text-right tabular-nums text-xs">
                      {r.status === "COMPUTED" && `CA ${r.ca_brut.toFixed(2)} · partenaire ${Number(r.partner_amount || 0).toFixed(2)}`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Fermer</Button>
          <Button onClick={run} disabled={!contractId || busy || Number(mTo) < Number(mFrom)} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="pc-batch-run">
            {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Calculer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
